/**
 * Source-aware CTAs for the creator profile header. The mapping below is
 * the spec — edit it (not Header.tsx) to change which actions appear where.
 *
 * Layout rule applied to the configs below:
 *   - Total CTAs ≤ 4 → all visible (primary + secondaries, no overflow)
 *   - Total CTAs ≥ 5 → primary + 2 secondaries + overflow with the rest
 */

export const PROFILE_SOURCE = {
  CRM: 'crm',
  APPLICATIONS: 'applications',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  ASSETS: 'assets',
  STATISTICS: 'statistics',
  STANDALONE: 'standalone',
} as const;

export type ProfileSource =
  (typeof PROFILE_SOURCE)[keyof typeof PROFILE_SOURCE];

export const CTA_ID = {
  INVITE_TO_CAMPAIGN: 'invite-to-campaign',
  INVITE_TO_ANOTHER_CAMPAIGN: 'invite-to-another-campaign',
  INVITE_TO_NEXT_CAMPAIGN: 'invite-to-next-campaign',
  ADD_TO_LIST: 'add-to-list',
  MESSAGE: 'message',
  VET: 'vet',
  RECRUIT_TO_CRM: 'recruit-to-crm',
  SHARE: 'share',
  ADD_NOTE: 'add-note',
  ACCEPT: 'accept',
  REJECT: 'reject',
  NEGOTIATE: 'negotiate',
  WITHDRAW_OFFER: 'withdraw-offer',
  ADD_TO_SHORTLIST: 'add-to-shortlist',
} as const;

export type CtaId = (typeof CTA_ID)[keyof typeof CTA_ID];

export type CtaTier = 'primary' | 'secondary' | 'overflow';

export interface SourceCtaConfig {
  primary: CtaId;
  secondary: CtaId[];
  overflow: CtaId[];
}

export const SOURCE_CTA_CONFIG: Record<ProfileSource, SourceCtaConfig> = {
  [PROFILE_SOURCE.CRM]: {
    primary: CTA_ID.INVITE_TO_CAMPAIGN,
    secondary: [CTA_ID.ADD_TO_LIST, CTA_ID.MESSAGE],
    overflow: [
      CTA_ID.VET,
      CTA_ID.RECRUIT_TO_CRM,
      CTA_ID.SHARE,
      CTA_ID.ADD_NOTE,
    ],
  },
  [PROFILE_SOURCE.APPLICATIONS]: {
    primary: CTA_ID.ACCEPT,
    // Negotiate and Withdraw share a slot — visibility flags in Header.tsx
    // make them mutually exclusive (Withdraw only when there's a pending
    // offer). Listing both keeps the row size constant in either state.
    secondary: [CTA_ID.REJECT, CTA_ID.NEGOTIATE, CTA_ID.WITHDRAW_OFFER],
    overflow: [
      CTA_ID.VET,
      CTA_ID.ADD_TO_SHORTLIST,
      CTA_ID.MESSAGE,
      CTA_ID.ADD_TO_LIST,
      CTA_ID.SHARE,
    ],
  },
  [PROFILE_SOURCE.ACCEPTED]: {
    primary: CTA_ID.MESSAGE,
    secondary: [CTA_ID.VET, CTA_ID.ADD_TO_LIST, CTA_ID.SHARE],
    overflow: [],
  },
  [PROFILE_SOURCE.DECLINED]: {
    primary: CTA_ID.MESSAGE,
    secondary: [CTA_ID.ADD_TO_LIST, CTA_ID.SHARE],
    overflow: [],
  },
  [PROFILE_SOURCE.ASSETS]: {
    primary: CTA_ID.MESSAGE,
    secondary: [CTA_ID.SHARE, CTA_ID.VET, CTA_ID.ADD_TO_LIST],
    overflow: [],
  },
  [PROFILE_SOURCE.STATISTICS]: {
    primary: CTA_ID.MESSAGE,
    secondary: [CTA_ID.INVITE_TO_NEXT_CAMPAIGN, CTA_ID.ADD_TO_LIST],
    overflow: [CTA_ID.VET, CTA_ID.SHARE],
  },
  [PROFILE_SOURCE.STANDALONE]: {
    primary: CTA_ID.INVITE_TO_CAMPAIGN,
    secondary: [CTA_ID.ADD_TO_LIST, CTA_ID.MESSAGE],
    overflow: [CTA_ID.VET, CTA_ID.SHARE, CTA_ID.RECRUIT_TO_CRM],
  },
};

/** Sources scoped to a campaign route — drive applicantStatus refinement. */
export const CAMPAIGN_SCOPED_SOURCES: ProfileSource[] = [
  PROFILE_SOURCE.APPLICATIONS,
  PROFILE_SOURCE.ACCEPTED,
  PROFILE_SOURCE.DECLINED,
  PROFILE_SOURCE.ASSETS,
  PROFILE_SOURCE.STATISTICS,
];

export function getSourceCtaConfig(source: ProfileSource): SourceCtaConfig {
  return (
    SOURCE_CTA_CONFIG[source] ?? SOURCE_CTA_CONFIG[PROFILE_SOURCE.STANDALONE]
  );
}
